/**
 * Three-way split of a client's capital so they always know what's locked
 * and what's actually withdrawable:
 *   • Seed Capital       — the first tier they bought (locked base)
 *   • Upgrade Credits    — total of every tier top-up / upgrade since (locked)
 *   • Withdrawable       — equity above (seed + upgrades) = pure profits
 * Shown on the terminal next to the standard stats table.
 */
export default function SeedBreakdownCard({ account }) {
  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const seed = Number(account?.original_seed ?? account?.seed_capital ?? 0);
  const upgrades = Number(account?.upgrade_credits ?? 0);
  const withdrawable = Number(account?.withdrawable_usd ?? 0);
  const balance = Number(account?.balance_usd ?? 0);

  const cards = [
    {
      k: "Seed Capital",
      v: `$${fmt(seed)}`,
      hint: "Your starting tier · locked",
      tone: "neutral",
      testid: "breakdown-seed",
    },
    {
      k: "Upgrade Credits",
      v: `$${fmt(upgrades)}`,
      hint: upgrades > 0 ? "Added via tier upgrades · locked" : "No upgrades yet",
      tone: "gold",
      testid: "breakdown-upgrades",
    },
    {
      k: "Withdrawable",
      v: `$${fmt(withdrawable)}`,
      hint: "Profit only · after tiers deducted",
      tone: withdrawable > 0 ? "buy" : "neutral",
      testid: "breakdown-withdrawable",
    },
  ];

  return (
    <div
      className="mt-4 border border-gold/25 bg-surface"
      data-testid="seed-breakdown-card"
    >
      <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
        <p className="text-[10px] uppercase tracking-[0.28em] text-gold">
          Capital breakdown
        </p>
        <p
          className="font-tabular text-[11px] text-white/50"
          data-testid="breakdown-balance"
        >
          Balance ${fmt(balance)}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-px bg-white/[0.03] sm:grid-cols-3">
        {cards.map((c) => (
          <div
            key={c.k}
            className="bg-surface p-5"
            data-testid={c.testid}
          >
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">
              {c.k}
            </p>
            <p
              className={`mt-1 font-tabular text-xl ${
                c.tone === "buy"
                  ? "text-[#26A69A]"
                  : c.tone === "gold"
                  ? "text-gold"
                  : "text-white"
              }`}
            >
              {c.v}
            </p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/35">
              {c.hint}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
